import React, { useEffect, useRef, useState } from 'react';

interface GoogleAdInterstitialProps {
  isOpen: boolean;
  onClose: () => void;
  duration?: number; // Duration in seconds
}

const GoogleAdInterstitial: React.FC<GoogleAdInterstitialProps> = ({
  isOpen,
  onClose,
  duration = 15
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(duration);
  const [canClose, setCanClose] = useState(false);
  const [adLoaded, setAdLoaded] = useState(false);
  const [showFallbackAd, setShowFallbackAd] = useState(false);
  const adRef = useRef<HTMLDivElement>(null); // For the ad container
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Load AdSense ad when modal opens
  useEffect(() => {
    if (isOpen && adRef.current) {
      // Create fresh ad element each time
      const adContainer = adRef.current;

      // Clear any existing ad
      adContainer.innerHTML = '';

      // Create new ins element
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.setAttribute('data-ad-client', 'ca-pub-5766742203172087');
      ins.setAttribute('data-ad-slot', '3291567929');
      ins.setAttribute('data-ad-format', 'auto');
      ins.setAttribute('data-full-width-responsive', 'true');

      adContainer.appendChild(ins);

      // Load the ad after a small delay
      const loadTimeout = setTimeout(() => {
        try {
          if ((window as any).adsbygoogle) {
            ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
          }

          // Check if AdSense actually loaded after 2 seconds
          setTimeout(() => {
            const insElement = adContainer.querySelector('ins.adsbygoogle');
            if (insElement) {
              const height = insElement.clientHeight;
              const width = insElement.clientWidth;

              // If ad is 0x0, AdSense failed - show fallback
              if (height === 0 || width === 0) {
                console.log('AdSense failed to load (0x0), showing fallback ad');
                setShowFallbackAd(true);
              }
            }
            setAdLoaded(true);
          }, 2000);
        } catch (e) {
          console.log('AdSense error:', e);
          setShowFallbackAd(true);
          setAdLoaded(true);
        }
      }, 300);

      return () => clearTimeout(loadTimeout);
    } else if (!isOpen) {
      setAdLoaded(false);
      setShowFallbackAd(false);
    }
  }, [isOpen]);

  // Countdown timer logic - only starts when ad is loaded
  useEffect(() => {
    if (!isOpen || !adLoaded) {
      setSecondsRemaining(duration);
      setCanClose(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start countdown now that ad is loaded
    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          setCanClose(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, adLoaded, duration]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick} style={{ zIndex: 10000 }}>
      <div className="modal-content" style={{ maxWidth: '728px', width: '90%' }}>
        <div className="modal-header">
          <h2 className="modal-title">Support Larry's Help</h2>
        </div>

        <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ marginBottom: '1.5rem', fontSize: '1rem', color: '#6b7280' }}>
            Please give us a few seconds to work on those calculations!
          </p>

          {/* Ad container - dynamically filled by useEffect */}
          {!showFallbackAd ? (
            <div
              ref={adRef}
              style={{
                minHeight: '250px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          ) : (
            <a
              href="https://connactiveclub.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img
                src="/local_advertisement.png"
                alt="Connactive Club - Find venues, organize leagues, connect with players"
                style={{
                  maxWidth: '100%',
                  maxHeight:'40vh',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              />
            </a>
          )}

          <div style={{ marginTop: '1.5rem' }}>
            {!adLoaded ? (
              <p style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                Loading advertisement...
              </p>
            ) : !canClose ? (
              <p style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                You can continue in <strong>{secondsRemaining}</strong> {secondsRemaining === 1 ? 'second' : 'seconds'}...
              </p>
            ) : (
              <button
                className="modal-button modal-button-primary"
                onClick={handleClose}
                style={{ fontSize: '1rem', padding: '0.75rem 2rem' }}
              >
                Continue to Calculator
              </button>
            )}
          </div>

          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#9ca3af' }}>
            Faster Calcs?{' '}
            <a href="/account" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
              Become a Supporter
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdInterstitial;
